import { z } from "zod";
import { SitewideSummary } from "./api/digest";
import { ResourcesPostPreviewSchema } from "./api/resources";
import { RSSFeedItem } from "./api/rss-feeds";
import { ChartSummary } from "./chart";
import { CompassEvent } from "./compassEvent";
import { DiscussionListEntry } from "./discussion";
import { Event } from "./event";
import { Post } from "./posts";
import { VendorListingEntry } from "./vendor";

/**
 * Discriminated union for favorite targets.
 * Each target type carries its correct ID type.
 */
export const FavoriteTarget = z.discriminatedUnion("type", [
    z.object({ type: z.literal("post"), id: z.number() }),
    z.object({ type: z.literal("feedArticle"), id: z.number() }),
    z.object({ type: z.literal("blogPost"), id: z.number() }),
    z.object({ type: z.literal("discussion"), id: z.number() }),
    z.object({ type: z.literal("chart"), id: z.number() }),
    z.object({ type: z.literal("event"), id: z.number() }),
    z.object({ type: z.literal("compassEvent"), id: z.number() }),
    z.object({ type: z.literal("vendor"), id: z.number() }),
    z.object({ type: z.literal("sitewideSummary"), id: z.number() }),
]);

export type FavoriteTarget = z.infer<typeof FavoriteTarget>;

export const FavoriteTargetType = z.enum([
    "post",
    "feedArticle",
    "blogPost",
    "discussion",
    "chart",
    "event",
    "compassEvent",
    "vendor",
    "sitewideSummary",
]);

export type FavoriteTargetType = z.infer<typeof FavoriteTargetType>;

/**
 * Discriminated union for favorites listing.
 * Each variant wraps the full shared schema for that entity type.
 */
export const FavoriteListItem = z.discriminatedUnion("type", [
    z.object({
        type: z.literal("post"),
        favoriteId: z.number(),
        favoritedAt: z.string(),
        data: Post,
    }),
    z.object({
        type: z.literal("feedArticle"),
        favoriteId: z.number(),
        favoritedAt: z.string(),
        data: RSSFeedItem,
    }),
    z.object({
        type: z.literal("blogPost"),
        favoriteId: z.number(),
        favoritedAt: z.string(),
        data: ResourcesPostPreviewSchema,
    }),
    z.object({
        type: z.literal("discussion"),
        favoriteId: z.number(),
        favoritedAt: z.string(),
        data: DiscussionListEntry,
    }),
    z.object({
        type: z.literal("chart"),
        favoriteId: z.number(),
        favoritedAt: z.string(),
        data: ChartSummary,
    }),
    z.object({
        type: z.literal("event"),
        favoriteId: z.number(),
        favoritedAt: z.string(),
        data: Event,
    }),
    z.object({
        type: z.literal("compassEvent"),
        favoriteId: z.number(),
        favoritedAt: z.string(),
        data: CompassEvent,
    }),
    z.object({
        type: z.literal("vendor"),
        favoriteId: z.number(),
        favoritedAt: z.string(),
        data: VendorListingEntry,
    }),
    z.object({
        type: z.literal("sitewideSummary"),
        favoriteId: z.number(),
        favoritedAt: z.string(),
        data: SitewideSummary,
    }),
]);

export type FavoriteListItem = z.infer<typeof FavoriteListItem>;

/**
 * Discriminated union for trending listing.
 * Same full entity data as FavoriteListItem, but with score instead of favorite metadata.
 * Only public content types (no thread, chart, compassEvent).
 */
export const TrendingListItem = z.discriminatedUnion("type", [
    z.object({ type: z.literal("post"), score: z.number(), data: Post }),
    z.object({ type: z.literal("feedArticle"), score: z.number(), data: RSSFeedItem }),
    z.object({ type: z.literal("blogPost"), score: z.number(), data: ResourcesPostPreviewSchema }),
    z.object({ type: z.literal("event"), score: z.number(), data: Event }),
    z.object({ type: z.literal("vendor"), score: z.number(), data: VendorListingEntry }),
    z.object({ type: z.literal("sitewideSummary"), score: z.number(), data: SitewideSummary }),
]);

export type TrendingListItem = z.infer<typeof TrendingListItem>;

export const PRIVATE_FAVORITE_TYPES: FavoriteTargetType[] = ["discussion", "chart", "compassEvent"];
export const PUBLIC_FAVORITE_TYPES: FavoriteTargetType[] = [
    "post",
    "feedArticle",
    "blogPost",
    "event",
    "vendor",
    "sitewideSummary",
];

export const FavoritesSearchSchema = z.object({
    types: z
        .string()
        .optional()
        .transform((val) => {
            if (!val) return undefined;
            const valid = [
                ...new Set(
                    val
                        .split(",")
                        .filter((v): v is FavoriteTargetType =>
                            FavoriteTargetType.options.includes(v as FavoriteTargetType),
                        ),
                ),
            ];
            return valid.length > 0 ? valid.join(",") : undefined;
        })
        .catch(undefined),
    page: z.coerce.number().int().min(1).optional().catch(undefined),
});
export type FavoritesSearchParams = z.infer<typeof FavoritesSearchSchema>;

export const TrendingSearchSchema = z.object({
    types: z
        .string()
        .optional()
        .transform((val) => {
            if (!val) return undefined;
            const valid = [
                ...new Set(
                    val
                        .split(",")
                        .filter((v): v is FavoriteTargetType =>
                            FavoriteTargetType.options.includes(v as FavoriteTargetType),
                        ),
                ),
            ];
            return valid.length > 0 ? valid.join(",") : undefined;
        })
        .catch(undefined),
});
export type TrendingSearchParams = z.infer<typeof TrendingSearchSchema>;
